import { numberParam, optionalNumberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  assessAiManipulation,
  AUTHENTICITY_WEIGHTS,
  confidenceWord,
  DISCLAIMER,
  judgeRisk,
} from '../authenticity/index.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * «Цифровая обработка (AI)».
 *
 * The check is an *ensemble*, not a classifier: it reports the combined risk of
 * independent, explainable signals and always tells the user which of them
 * fired. There is no offline AI detector, so a generated image can never be
 * "proved" — the wording stays a warning, and the AI-classifier absence is
 * listed in `details.unavailable`.
 *
 * The verdict is `warning` at most unless the profile explicitly opts into a
 * failure with `params.failRisk` (and even then `params.failConfidence` must be
 * cleared). Low ensemble confidence always degrades to `uncertain`.
 */
export const aiManipulationCheck: ValidationCheck = {
  id: 'ai_manipulation',
  title: 'Цифровая обработка (AI)',
  description:
    'Совокупность косвенных признаков цифровой обработки: следы пересжатия и пересэмплинга, несоответствие текстуры и шума кожи фону, копирование фрагментов, метаданные редакторов.',
  kind: 'content',
  requires: ['pixels', 'quality'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'quality']);
    if (missing) return notChecked(ctx, aiManipulationCheck, missing, ctx.rule.severity);

    const assessment = assessAiManipulation(ctx);
    const warnRisk = numberParam(ctx.rule.params, 'warnRisk', 0.4);
    const uncertainBelowConfidence = numberParam(
      ctx.rule.params,
      'uncertainBelowConfidence',
      0.45,
    );
    const failRisk = optionalNumberParam(ctx.rule.params, 'failRisk');
    const failConfidence = numberParam(ctx.rule.params, 'failConfidence', 0.8);

    const risk = assessment.risk;
    const confidence = assessment.confidence;
    const verdict = judgeRisk({
      risk,
      confidence,
      warnRisk,
      uncertainBelowConfidence,
      ...(failRisk !== undefined ? { failRisk } : {}),
      failConfidence,
    });
    const word = confidenceWord(confidence);
    const findings =
      assessment.findings.length > 0 ? assessment.findings : ['явных признаков не обнаружено'];

    const message =
      verdict.status === 'pass'
        ? '✅ Признаков цифровой обработки не обнаружено'
        : verdict.status === 'warning'
          ? '⚠ Возможны признаки цифровой обработки'
          : verdict.status === 'fail'
            ? 'Обнаружены выраженные признаки цифровой обработки.'
            : 'Не удалось уверенно проверить признаки цифровой обработки.';


    return plainResult({
      id: aiManipulationCheck.id,
      title: aiManipulationCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      message,
      fixability: 'retake',
      confidence: roundTo(confidence, 2),
      details: {
        risk: roundTo(risk, 4),
        confidence: roundTo(confidence, 4),
        confidenceWord: word,
        signals: assessment.signals.map((signal) => ({
          id: signal.id,
          score: roundTo(signal.score, 4),
          confidence: roundTo(signal.confidence, 4),
          note: signal.note,
        })),
        findings,
        unavailable: assessment.unavailable,
        weights: { ...AUTHENTICITY_WEIGHTS },
        thresholds: {
          warnRisk,
          uncertainBelowConfidence,
          failRisk: failRisk ?? null,
          failConfidence,
        },
        verdictReason: verdict.reason,
        disclaimer: DISCLAIMER,
      },
    });
  },
};
